import { metresToFeet, msToKnots, prettyCallsign } from "../lib/format.js";
import "./SteamGauges.css";

/**
 * SteamGauges — a classic light-aircraft "six-pack" of analog steam gauges.
 *
 * Self-contained SVG. Round dial instruments with real needles, mounted on a
 * brushed-metal panel — NOT a glass cockpit. Driven by live ADS-B telemetry.
 *
 *   <SteamGauges aircraft={selectedAircraft} bankDeg={bank} />
 *
 * aircraft = {
 *   velocity (m/s), baroAltitude (m), heading (deg),
 *   verticalRate (m/s), onGround, callsign
 * } | null
 */

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const degrees = (rad) => (rad * 180) / Math.PI;
const wrap360 = (d) => ((d % 360) + 360) % 360;

// Palette — real GA instrument faces.
const FACE = "#0c0d10"; // black dial face
const MARK = "#e9edf2"; // white markings / numbers
const NEEDLE = "#f4f7fb"; // white needle
const POINTER = "#ffb020"; // orange bug / pointer
const DIM = "#3a3f47"; // "off" / dimmed markings

// --- Geometry helpers ----------------------------------------------------
const CX = 100;
const CY = 100;
const R = 92; // outer face radius (viewBox is 200x200)

/** angle in degrees measured clockwise from 12-o'clock -> x,y on circle */
function polar(cx, cy, r, angDeg) {
  const a = ((angDeg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
}

/** Map a value within [min,max] onto a sweep of dial angles [a0,a1] (deg). */
function valToAngle(v, min, max, a0, a1) {
  const t = clamp((v - min) / (max - min), 0, 1);
  return a0 + t * (a1 - a0);
}

// --- Small shared sub-components ----------------------------------------
function Bezel() {
  return (
    <>
      <circle cx={CX} cy={CY} r={99} className="sg-bezel-outer" />
      <circle cx={CX} cy={CY} r={95} className="sg-bezel-inner" />
      <circle cx={CX} cy={CY} r={R} fill={FACE} />
    </>
  );
}

function Needle({ angle, length = 74, tail = 16, width = 5, color = NEEDLE }) {
  return (
    <g transform={`rotate(${angle} ${CX} ${CY})`}>
      <polygon
        points={`${CX - width / 2},${CY + tail} ${CX + width / 2},${CY + tail} ${CX + 1.2},${CY - length} ${CX - 1.2},${CY - length}`}
        fill={color}
      />
      <circle cx={CX} cy={CY} r={6} fill="#1a1c20" stroke={color} strokeWidth="1.5" />
    </g>
  );
}

function Ticks({ min, max, a0, a1, major, minor, labelStep, labelFmt = (v) => v, offLabels = false }) {
  const ticks = [];
  const color = offLabels ? DIM : MARK;
  for (let v = min; v <= max + 1e-6; v += minor) {
    const isMajor = Math.abs((v - min) % major) < 1e-6 || Math.abs(v - max) < 1e-6;
    const ang = valToAngle(v, min, max, a0, a1);
    const rOuter = 88;
    const rInner = isMajor ? 76 : 82;
    const [x1, y1] = polar(CX, CY, rOuter, ang);
    const [x2, y2] = polar(CX, CY, rInner, ang);
    ticks.push(
      <line key={`t${v}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={isMajor ? 2 : 1} />
    );
    if (labelStep && Math.abs((v - min) % labelStep) < 1e-6) {
      const [lx, ly] = polar(CX, CY, 64, ang);
      ticks.push(
        <text key={`l${v}`} x={lx} y={ly} className="sg-num" fill={color}>
          {labelFmt(v)}
        </text>
      );
    }
  }
  return <g>{ticks}</g>;
}

function Readout({ text, off = false }) {
  return (
    <g>
      <rect x={CX - 30} y={CY + 44} width={60} height={20} rx={3} className="sg-readout-box" />
      <text x={CX} y={CY + 58} className="sg-readout" fill={off ? DIM : "#8fffa8"}>
        {text}
      </text>
    </g>
  );
}

// --- (1) Airspeed Indicator ---------------------------------------------
function AirspeedGauge({ ias, off }) {
  const MIN = 40, MAX = 200, A0 = -150, A1 = 150; // sweep angles
  const val = off ? MIN : clamp(ias ?? MIN, MIN, MAX);
  const angle = valToAngle(val, MIN, MAX, A0, A1);
  return (
    <svg viewBox="0 0 200 200" className={`sg-dial${off ? " sg-off" : ""}`} role="img"
      aria-label={`Airspeed indicator: ${off ? "no signal" : `${ias ?? 0} knots`}`}>
      <Bezel />
      <Ticks min={MIN} max={MAX} a0={A0} a1={A1} major={20} minor={10} labelStep={20} offLabels={off} />
      {/* coloured arcs — normal operating / caution range */}
      <path d={arc(60, 160, MIN, MAX, A0, A1)} className="sg-arc-green" fill="none" />
      <path d={arc(160, 190, MIN, MAX, A0, A1)} className="sg-arc-yellow" fill="none" />
      <text x={CX} y={CY - 40} className="sg-face-label" fill={off ? DIM : MARK}>KNOTS</text>
      {!off && <Needle angle={angle} color={NEEDLE} />}
      {off && <circle cx={CX} cy={CY} r={6} fill="#1a1c20" stroke={DIM} strokeWidth="1.5" />}
      <Readout text={off ? "OFF" : `${ias ?? 0} kt`} off={off} />
    </svg>
  );
}

function arc(vFrom, vTo, MIN, MAX, A0, A1, radius = 90) {
  // Colour band along the rim between two scale values (styled via CSS stroke).
  const a0 = valToAngle(vFrom, MIN, MAX, A0, A1);
  const a1 = valToAngle(vTo, MIN, MAX, A0, A1);
  const [x0, y0] = polar(CX, CY, radius, a0);
  const [x1, y1] = polar(CX, CY, radius, a1);
  const large = Math.abs(a1 - a0) > 180 ? 1 : 0;
  return `M ${x0} ${y0} A ${radius} ${radius} 0 ${large} 1 ${x1} ${y1}`;
}

// --- (2) Attitude Indicator (artificial horizon) ------------------------
function AttitudeGauge({ pitchDeg, bankDeg, off }) {
  const pitch = off ? 0 : pitchDeg;
  const bank = off ? 0 : bankDeg;
  const PPX = 2.6; // px per degree of pitch
  const dy = pitch * PPX;
  const clipId = "sg-adi-clip";
  return (
    <svg viewBox="0 0 200 200" className={`sg-dial${off ? " sg-off" : ""}`} role="img"
      aria-label={`Attitude indicator: ${off ? "no signal" : `bank ${Math.round(bank)} degrees, pitch ${Math.round(pitch)} degrees`}`}>
      <defs>
        <clipPath id={clipId}>
          <circle cx={CX} cy={CY} r={R} />
        </clipPath>
      </defs>
      <Bezel />
      <g clipPath={`url(#${clipId})`}>
        {/* rotating + translating sky/ground disk */}
        <g transform={`rotate(${-bank} ${CX} ${CY}) translate(0 ${dy})`}>
          <rect x={-120} y={-220} width={440} height={320} fill={off ? "#22252b" : "#2f7fd1"} />
          <rect x={-120} y={100} width={440} height={320} fill={off ? "#1a1c20" : "#8a5a2b"} />
          <line x1={-120} y1={100} x2={320} y2={100} stroke={MARK} strokeWidth="2" />
          {/* pitch ladder */}
          {[-20, -10, 10, 20].map((p) => {
            const yy = 100 - p * PPX;
            const w = p % 20 === 0 ? 34 : 20;
            return (
              <g key={p} stroke={MARK} strokeWidth="1.5">
                <line x1={CX - w} y1={yy} x2={CX + w} y2={yy} />
                <text x={CX + w + 6} y={yy + 3} className="sg-pitch-num" fill={MARK}>{Math.abs(p)}</text>
                <text x={CX - w - 6} y={yy + 3} className="sg-pitch-num" fill={MARK} textAnchor="end">{Math.abs(p)}</text>
              </g>
            );
          })}
        </g>
      </g>
      {/* bank scale arc + pointer (fixed to case) */}
      <g stroke={MARK} strokeWidth="1.5" fill="none">
        {[-60, -45, -30, -20, -10, 10, 20, 30, 45, 60].map((b) => {
          const [x1, y1] = polar(CX, CY, 88, b);
          const [x2, y2] = polar(CX, CY, Math.abs(b) % 30 === 0 ? 80 : 84, b);
          return <line key={b} x1={x1} y1={y1} x2={x2} y2={y2} />;
        })}
      </g>
      {/* sky pointer triangle rotates with bank */}
      <g transform={`rotate(${-bank} ${CX} ${CY})`}>
        <polygon points={`${CX},${CY - 88} ${CX - 6},${CY - 78} ${CX + 6},${CY - 78}`} fill={POINTER} />
      </g>
      {/* fixed miniature aircraft */}
      <g stroke={POINTER} strokeWidth="3" fill="none">
        <line x1={CX - 34} y1={CY} x2={CX - 12} y2={CY} />
        <line x1={CX + 12} y1={CY} x2={CX + 34} y2={CY} />
        <circle cx={CX} cy={CY} r={3} fill={POINTER} stroke="none" />
        <line x1={CX} y1={CY} x2={CX} y2={CY + 10} />
      </g>
      <Readout text={off ? "OFF" : `${bank >= 0 ? "R" : "L"}${Math.abs(Math.round(bank))}°`} off={off} />
    </svg>
  );
}

// --- (3) Altimeter ------------------------------------------------------
function AltimeterGauge({ altFt, off }) {
  const ft = off ? 0 : altFt ?? 0;
  // hundreds hand: 0-1000 ft per rev; thousands hand: 0-10000 per rev.
  const hundreds = ((ft % 1000) / 1000) * 360;
  const thousands = ((ft % 10000) / 10000) * 360;
  return (
    <svg viewBox="0 0 200 200" className={`sg-dial${off ? " sg-off" : ""}`} role="img"
      aria-label={`Altimeter: ${off ? "no signal" : `${(altFt ?? 0).toLocaleString()} feet`}`}>
      <Bezel />
      {/* 0..900 in hundreds, 10 major marks */}
      <Ticks min={0} max={1000} a0={0} a1={360} major={100} minor={20} labelStep={100}
        labelFmt={(v) => (v === 1000 ? "" : v / 100)} offLabels={off} />
      <text x={CX} y={CY - 44} className="sg-face-label" fill={off ? DIM : MARK}>ALT</text>
      <text x={CX} y={CY + 34} className="sg-face-sub" fill={off ? DIM : MARK}>100 FEET</text>
      {/* Kollsman window */}
      <rect x={CX + 24} y={CY - 8} width={40} height={16} rx={2} className="sg-readout-box" />
      <text x={CX + 44} y={CY + 4} className="sg-kollsman" fill={off ? DIM : MARK}>29.92</text>
      {!off && (
        <>
          {/* short = thousands (fat, short) */}
          <Needle angle={thousands} length={46} tail={12} width={8} color={NEEDLE} />
          {/* long = hundreds (thin, long) */}
          <Needle angle={hundreds} length={82} tail={16} width={4} color={NEEDLE} />
        </>
      )}
      {off && <circle cx={CX} cy={CY} r={6} fill="#1a1c20" stroke={DIM} strokeWidth="1.5" />}
      <Readout text={off ? "OFF" : `${(altFt ?? 0).toLocaleString()} ft`} off={off} />
    </svg>
  );
}

// --- (4) Turn Coordinator -----------------------------------------------
function TurnCoordinatorGauge({ turnRate, bankDeg, off }) {
  // turnRate deg/s -> symbol bank. Standard rate (3°/s) aligns to the mark.
  const symBank = off ? 0 : clamp(turnRate * 6, -25, 25);
  // slip/skid ball: proxy from bank (uncoordinated shows offset).
  const ball = off ? 0 : clamp(bankDeg * 0.6, -18, 18);
  return (
    <svg viewBox="0 0 200 200" className={`sg-dial${off ? " sg-off" : ""}`} role="img"
      aria-label={`Turn coordinator: ${off ? "no signal" : `${turnRate >= 0 ? "right" : "left"} turn ${Math.abs(turnRate).toFixed(1)} degrees per second`}`}>
      <Bezel />
      {/* standard-rate index marks (wingtip level, left & right) */}
      <g stroke={MARK} strokeWidth="2">
        {[-30, 30].map((m) => {
          const [x1, y1] = polar(CX, CY, 88, 90 + m);
          const [x2, y2] = polar(CX, CY, 78, 90 + m);
          return <line key={m} x1={x1} y1={y1} x2={x2} y2={y2} />;
        })}
        <line x1={22} y1={CY} x2={34} y2={CY} />
        <line x1={166} y1={CY} x2={178} y2={CY} />
      </g>
      <text x={CX} y={CY - 40} className="sg-face-label" fill={off ? DIM : MARK}>TURN</text>
      <text x={40} y={CY - 22} className="sg-tc-lr" fill={off ? DIM : MARK}>L</text>
      <text x={160} y={CY - 22} className="sg-tc-lr" fill={off ? DIM : MARK}>R</text>
      {/* banking little aircraft */}
      <g transform={`rotate(${symBank} ${CX} ${CY})`} stroke={off ? DIM : NEEDLE} strokeWidth="3.5" fill="none">
        <line x1={CX - 40} y1={CY} x2={CX + 40} y2={CY} />
        <circle cx={CX} cy={CY} r={5} fill={off ? DIM : NEEDLE} stroke="none" />
        <line x1={CX} y1={CY} x2={CX} y2={CY - 22} />
      </g>
      {/* inclinometer (slip/skid ball) tube */}
      <g>
        <rect x={CX - 26} y={CY + 30} width={52} height={16} rx={8} className="sg-tube" />
        <line x1={CX - 6} y1={CY + 30} x2={CX - 6} y2={CY + 46} stroke={MARK} strokeWidth="1" />
        <line x1={CX + 6} y1={CY + 30} x2={CX + 6} y2={CY + 46} stroke={MARK} strokeWidth="1" />
        <circle cx={CX + ball} cy={CY + 38} r={6} fill={off ? DIM : "#e9edf2"} />
      </g>
      <text x={CX} y={CY - 56} className="sg-tc-note" fill={off ? DIM : MARK}>NO PITCH INFORMATION</text>
    </svg>
  );
}

// --- (5) Heading Indicator (directional gyro) ---------------------------
function HeadingGauge({ heading, off }) {
  const hdg = off ? 0 : wrap360(heading ?? 0);
  const cardRot = -hdg; // rotate card so current heading sits under lubber line
  const marks = [];
  for (let d = 0; d < 360; d += 5) {
    const isMajor = d % 30 === 0;
    const [x1, y1] = polar(CX, CY, 88, d);
    const [x2, y2] = polar(CX, CY, isMajor ? 76 : 82, d);
    marks.push(<line key={`m${d}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke={off ? DIM : MARK} strokeWidth={isMajor ? 2 : 1} />);
    if (isMajor) {
      const [lx, ly] = polar(CX, CY, 64, d);
      const label = { 0: "N", 90: "E", 180: "S", 270: "W" }[d] ?? d / 10;
      marks.push(
        <text key={`ml${d}`} x={lx} y={ly} className="sg-num" fill={off ? DIM : MARK}
          transform={`rotate(${d} ${lx} ${ly})`}>{label}</text>
      );
    }
  }
  return (
    <svg viewBox="0 0 200 200" className={`sg-dial${off ? " sg-off" : ""}`} role="img"
      aria-label={`Heading indicator: ${off ? "no signal" : `${Math.round(hdg)} degrees`}`}>
      <Bezel />
      <g transform={`rotate(${cardRot} ${CX} ${CY})`}>{marks}</g>
      {/* fixed lubber line + yellow aircraft symbol */}
      <polygon points={`${CX},6 ${CX - 6},18 ${CX + 6},18`} fill={POINTER} />
      <g stroke={POINTER} strokeWidth="3" fill="none">
        <line x1={CX} y1={CY - 26} x2={CX} y2={CY + 26} />
        <line x1={CX - 20} y1={CY + 4} x2={CX + 20} y2={CY + 4} />
        <line x1={CX - 10} y1={CY + 22} x2={CX + 10} y2={CY + 22} />
      </g>
      <Readout text={off ? "OFF" : `${String(Math.round(hdg)).padStart(3, "0")}°`} off={off} />
    </svg>
  );
}

// --- (6) Vertical Speed Indicator ---------------------------------------
function VerticalSpeedGauge({ vsFpm, off }) {
  const MIN = -2000, MAX = 2000, A0 = -240, A1 = 60; // 0 at 9 o'clock-ish sweep
  // Map so 0 is at left (9 o'clock), +2000 top, -2000 bottom — classic VSI.
  const val = off ? 0 : clamp(vsFpm ?? 0, MIN, MAX);
  const angle = valToAngle(val, MIN, MAX, A0, A1);
  const labels = [-20, -15, -10, -5, 0, 5, 10, 15, 20]; // hundreds of fpm
  return (
    <svg viewBox="0 0 200 200" className={`sg-dial${off ? " sg-off" : ""}`} role="img"
      aria-label={`Vertical speed indicator: ${off ? "no signal" : `${(vsFpm ?? 0).toLocaleString()} feet per minute`}`}>
      <Bezel />
      {labels.map((l) => {
        const v = l * 100;
        const ang = valToAngle(v, MIN, MAX, A0, A1);
        const [x1, y1] = polar(CX, CY, 88, ang);
        const [x2, y2] = polar(CX, CY, 76, ang);
        const [lx, ly] = polar(CX, CY, 62, ang);
        return (
          <g key={l}>
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={off ? DIM : MARK} strokeWidth="2" />
            <text x={lx} y={ly} className="sg-num" fill={off ? DIM : MARK}>{Math.abs(l)}</text>
          </g>
        );
      })}
      {/* minor ticks */}
      {Array.from({ length: 41 }, (_, i) => MIN + i * 100).map((v) => {
        if (v % 500 === 0) return null;
        const ang = valToAngle(v, MIN, MAX, A0, A1);
        const [x1, y1] = polar(CX, CY, 88, ang);
        const [x2, y2] = polar(CX, CY, 82, ang);
        return <line key={`mn${v}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke={off ? DIM : MARK} strokeWidth="1" />;
      })}
      <text x={CX} y={CY - 42} className="sg-face-sub" fill={off ? DIM : MARK}>UP</text>
      <text x={CX} y={CY + 52} className="sg-face-sub" fill={off ? DIM : MARK}>DOWN</text>
      <text x={CX} y={CY - 20} className="sg-face-label" fill={off ? DIM : MARK}>100 FT/MIN</text>
      {!off && <Needle angle={angle} length={80} tail={12} width={4} color={NEEDLE} />}
      {off && <circle cx={CX} cy={CY} r={6} fill="#1a1c20" stroke={DIM} strokeWidth="1.5" />}
      <Readout text={off ? "OFF" : `${vsFpm > 0 ? "+" : ""}${(vsFpm ?? 0).toLocaleString()}`} off={off} />
    </svg>
  );
}

// --- Instrument cell (bezel + label) ------------------------------------
function Cell({ label, children }) {
  return (
    <div className="sg-cell">
      <div className="sg-cell-ring">{children}</div>
      <div className="sg-cell-label">{label}</div>
    </div>
  );
}

// --- Panel --------------------------------------------------------------
export default function SteamGauges({ aircraft, bankDeg = 0 }) {
  const off = !aircraft;

  const ias = off ? null : msToKnots(aircraft.velocity);
  const altFt = off ? null : metresToFeet(aircraft.baroAltitude);
  const vr = off ? 0 : aircraft.verticalRate ?? 0;
  const vsFpm = off ? 0 : Math.round(vr * 196.85);
  const velocity = off ? 0 : aircraft.velocity ?? 0;
  const heading = off ? 0 : aircraft.heading ?? 0;

  const pitchDeg =
    !off && velocity > 10
      ? clamp(degrees(Math.asin(clamp(vr / velocity, -1, 1))), -15, 15)
      : 0;

  // Turn rate proxy from bank: standard coordinated-turn approximation
  // omega ≈ (g * tan(bank)) / TAS  (deg/s). Fall back to a simple scale.
  const tasMs = Math.max(velocity, 30);
  const turnRate = off
    ? 0
    : clamp(degrees((9.81 * Math.tan((bankDeg * Math.PI) / 180)) / tasMs), -6, 6);

  const callsign = off ? "—" : prettyCallsign(aircraft.callsign);

  return (
    <div className="sg-panel" role="group" aria-label="Analog steam gauge instrument panel">
      <div className="sg-panel-header">
        <span className="sg-panel-title">FLIGHT INSTRUMENTS</span>
        <span className="sg-panel-callsign">{callsign}</span>
      </div>
      <div className="sg-grid">
        <Cell label="AIRSPEED"><AirspeedGauge ias={ias} off={off} /></Cell>
        <Cell label="ATTITUDE"><AttitudeGauge pitchDeg={pitchDeg} bankDeg={bankDeg} off={off} /></Cell>
        <Cell label="ALTITUDE"><AltimeterGauge altFt={altFt} off={off} /></Cell>
        <Cell label="TURN COORDINATOR"><TurnCoordinatorGauge turnRate={turnRate} bankDeg={bankDeg} off={off} /></Cell>
        <Cell label="HEADING"><HeadingGauge heading={heading} off={off} /></Cell>
        <Cell label="VERTICAL SPEED"><VerticalSpeedGauge vsFpm={vsFpm} off={off} /></Cell>
      </div>
    </div>
  );
}
